import { useState } from 'react'
import { ChevronRight, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useSelectableDepartmentTree } from '@/hooks/useDepartments'
import type { ApiDepartment } from '@/types/department'

interface DepartmentSelectFieldProps {
  value: number | null
  onChange: (id: number | null) => void
}

// 递归查找部门名称
function findDepartmentName(nodes: ApiDepartment[], id: number): string | null {
  for (const node of nodes) {
    if (node.id === id) return node.name
    if (node.children) {
      const found = findDepartmentName(node.children, id)
      if (found) return found
    }
  }
  return null
}

// 紧凑树节点
function CompactTreeNode({
  node,
  selectedId,
  onSelect,
  level = 0,
}: {
  node: ApiDepartment
  selectedId: number | null
  onSelect: (id: number) => void
  level?: number
}) {
  const [isExpanded, setIsExpanded] = useState(level < 2)
  const hasChildren = node.children && node.children.length > 0
  const isSelected = selectedId === node.id

  return (
    <div className="select-none">
      <div
        className={cn(
          'flex items-center gap-1 px-2 py-1.5 cursor-pointer rounded-sm transition-colors',
          isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(node.id)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (hasChildren) setIsExpanded(!isExpanded)
          }}
          className={cn(
            'h-4 w-4 flex items-center justify-center shrink-0',
            !hasChildren && 'invisible'
          )}
        >
          {hasChildren && (
            isExpanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )
          )}
        </button>
        <span className="flex-1 truncate text-sm">{node.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <CompactTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function DepartmentSelectField({ value, onChange }: DepartmentSelectFieldProps) {
  const [open, setOpen] = useState(false)
  const { data: treeData, isLoading } = useSelectableDepartmentTree()

  const selectedName = treeData && value ? findDepartmentName(treeData, value) : null

  const handleSelect = (id: number) => {
    onChange(id)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {selectedName ? (
            <span className="truncate">{selectedName}</span>
          ) : (
            <span className="text-muted-foreground">选择部门（可选）</span>
          )}
          {value !== null ? (
            <X className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100" onClick={handleClear} />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="max-h-64 overflow-auto py-1">
          {isLoading ? (
            <div className="px-2 space-y-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : !treeData || treeData.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">暂无部门数据</div>
          ) : (
            treeData.map((node) => (
              <CompactTreeNode
                key={node.id}
                node={node}
                selectedId={value}
                onSelect={handleSelect}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
